import { queryParamsToObject } from './query-params.utils';
import { isValidClearingDegreeQueryParam, isPartTimeCourse } from './clearing.utils';
import { Product, ProductEnum } from '@uc/web/shared/data-models';
import { CLE_UNI_SEARCH_SLUG } from '@uc/web/shared/data-access';

export const getRouteSegmentsAndQueryParams = (url: string) => {
	const route = url?.split('?');
	const segments = route[0]?.split('/').filter(Boolean);
	const queryParams = queryParamsToObject(route[1]) || {};

	return { segments, queryParams };
};

export function getCourseSearchSegments(
	isHECoSSubject: boolean,
	degree: string | undefined,
	searchTerm: string,
	location?: string | undefined,
) {
	const subject = searchTerm?.trim().replace(/ /g, '-').toLowerCase();

	if (degree === ProductEnum.Clearing) {
		return isHECoSSubject
			? new Set(['/courses', 'clearing', subject].filter(Boolean))
			: new Set(['/courses', 'search', degree].filter(Boolean));
	}

	return isHECoSSubject
		? new Set(['/courses', 'degrees', degree, subject, location].filter(Boolean))
		: new Set(['/courses', 'search', degree, location].filter(Boolean));
}

export const getClearingRouteParts = (
	isHECoSSubject: boolean,
	degree: string | undefined,
	subject: string,
	location: string | undefined,
	url: string,
) => {
	const segments = getCourseSearchSegments(
		isHECoSSubject,
		ProductEnum.Clearing,
		subject,
	);
	const { queryParams } = getRouteSegmentsAndQueryParams(url);

	if (location) {
		queryParams['location'] = location;
	}

	if (degree && isValidClearingDegreeQueryParam(degree)) {
		queryParams['degree'] = degree;
	}

	if (isPartTimeCourse(queryParams['mode'])) {
		delete queryParams['mode'];
	}

	return {
		segments,
		queryParams,
	};
};

export const determineSegmentsOnReset = (
	studyLevel: Product,
	isUniCourseSearch: boolean,
	isHECoSSubject: boolean,
	subject: string,
) => {
	let segments;
	if (studyLevel === ProductEnum.Clearing) {
		segments = determineClearingSegmentsOnFilterReset(
			isUniCourseSearch,
			isHECoSSubject,
			subject,
		);
	} else {
		segments = determineUgPGSegmentsOnFilterReset(
			studyLevel,
			isUniCourseSearch,
			isHECoSSubject,
			subject,
		);
	}

	return [...new Set(segments.filter(Boolean))];
};

const determineUgPGSegmentsOnFilterReset = (
	studyLevel: Product,
	isUniCourseSearch: boolean,
	isHECoSSubject: boolean,
	subject: string,
) => {
	if (isUniCourseSearch) {
		return ['/courses', studyLevel];
	} else if (!isHECoSSubject) {
		return ['/courses', 'search', studyLevel];
	}

	return ['/courses', 'degrees', studyLevel, subject];
};

const determineClearingSegmentsOnFilterReset = (
	isUniCourseSearch: boolean,
	isHECoSSubject: boolean,
	subject = '',
) => {
	const clearing = ProductEnum.Clearing;

	if (isUniCourseSearch) {
		return ['/courses', CLE_UNI_SEARCH_SLUG];
	} else if (!isHECoSSubject) {
		return ['/courses', 'search', clearing];
	}

	return ['/courses', clearing, subject];
};
